import { BlogPost } from '../types';

const post: BlogPost = {
	slug: 'hadaya-lil-akh-al-kabir-wal-ukht-al-kubra',
	title: 'هدايا للأخ الكبير أو الأخت الكبرى الجديدة (هدايا تُحدث فرقاً فعلياً)',
	description:
		'ماذا تُهدي الطفل الأكبر عند وصول مولود جديد، ولماذا تكون هدية «الأخ/الأخت الكبير» أهم مما تبدو، وكيف تختار التوقيت المناسب.',
	date: '2026-03-11',
	heroImage: '/assets/hero/kid-2/classic_scene.png',
	tags: ['أدلة الهدايا', 'مولود جديد'],
	body: `عندما يصل مولود جديد، يحصل الطفل الأكبر على صفقة غريبة. يمتلئ البيت بالزوار، ويذهب معظم الاهتمام تقريباً إلى شخص لا يستطيع بعد أن يرفع رأسه.

الهدية للطفل الأكبر في هذه اللحظة ليست عن الشيء المُهدى في الحقيقة. إنها عن منحه دوراً.

## لماذا تستحق هدية «الأخ/الأخت الكبير» التخطيط لها

الأطفال بين سنّي الثانية والسادسة يفهمون عموماً ما يجري، ولا يملكون أي استراتيجية للتعامل معه. لا أحد يُزيحهم فعلياً بأي معنى حقيقي، لكن هذا ليس استنتاجاً غير معقول لما يرونه أمامهم من مؤشرات.

منح الطفل الأكبر شيئاً يسمّيه — *الأخ الكبير*، *الأخت الكبرى* — يعيد صياغة الحدث بأكمله. فهو لم يعد الشخص الذي تُؤخذ منه الأشياء؛ بل أصبح الشخص الذي حصل على ترقية.

وينجح هذا بشكل أفضل حين تكون الهدية محددة لا عامة. قميص مكتوب عليه «الأخ الكبير» أمر جيد. أما كتاب يكون فيه الطفل الأكبر هو البطل المرسوم، والمولود الجديد شخصية مساندة في قصته *هو*، فيحقق أثراً أكبر بكثير.

## ماذا تُهدي

**كتاب مخصص يجسّده في دور الأخ أو الأخت الكبرى.** الخيار الأقوى على الإطلاق. يمنح الطفل سرداً له فيه دور بالفعل، وهو قابل لإعادة القراءة — وهذا مهم، لأن هذا شعور لا ينتهي في عصر يوم واحد.

**كتاب تلوين مخصص.** مفيد لمشكلة محددة في فترة المولود الجديد: فترات طويلة يكون فيها الوالد أو الوالدة مشغولين، ويحتاج الطفل الأكبر إلى شيء يستحوذ على انتباهه دون إشراف.

**تذكار يجمع الطفلين معاً.** هدية للوالدين أكثر منها للطفل، لكنها تبقى صامدة لعقود.

**شيء يخصّه هو وحده.** كوب، حقيبة قماشية، قميص بشخصيته الخاصة. الفكرة أن المولود الجديد لا حق له فيه على الإطلاق. أطفال الثالثة من العمر يأخذون هذا الأمر بجدية تامة.

## ما يجب تجنبه

- **أي شيء سيرثه المولود الجديد لاحقاً.** يقوّض الرسالة كلها.
- **هدايا تُقدَّم على أنها «من المولود».** يكتشف الأطفال الأكبر سناً هذه الحيلة فوراً، وغالباً ما تُفهم على أنها مهينة بعض الشيء.
- **ألعاب تحتاج إلى والد لتشغيلها.** وقت الوالدين وطاقتهما في الأسابيع الأولى هما أندر مورد في البيت.

## التوقيت

سلّموا الهدية في يوم عودة المولود إلى المنزل، أو في أول زيارة إلى المستشفى. تلك هي اللحظة التي يكون فيها الطفل الأكبر أكثر وعياً بالتغيير.

وهذا يعني الطلب مسبقاً. القطع المخصصة تُصنع حسب الطلب — توليد الرسوم، موافقتكم، الطباعة، الشحن — لذا اتركوا أسبوعين إلى ثلاثة أسابيع. وبما أن مواعيد الولادة تقريبية، فمن المنطقي الطلب قبل شهر كامل.

إن كنتم من الأقارب الزائرين لا الوالدين: أحضروا شيئاً للطفل الأكبر. فالجميع سيُحضر شيئاً للمولود، والطفل الأكبر يعدّ.

## ملاحظة حول الطفل الثاني

إذا فعلتم هذا مع الطفل الأول، فخططوا لفعله مع الثاني بعد بضع سنوات. الأطفال يحصون هذه الأمور بدقة لافتة، وعبارة «هناك كتاب عنك أنت ولا يوجد عني أنا» مظلمة طويلة العمر.`,
	faqs: [
		{
			question: 'ماذا تُهدي الطفل الأكبر عند وصول مولود جديد؟',
			answer:
				'شيئاً يخصّه هو حصراً ويمنحه اسماً لدوره الجديد. كتاب مخصص يجسّد الطفل الأكبر في دور الأخ الكبير أو الأخت الكبرى ينجح بشكل خاص، لأنه يمنحه دوراً سردياً لا مجرد لقب، ويمكن إعادة قراءته على مدى الأسابيع التالية.',
		},
		{
			question: 'متى يجب تقديم هدية الأخ أو الأخت الكبرى؟',
			answer:
				'في يوم عودة المولود إلى المنزل، أو في أول زيارة إلى المستشفى — اللحظة التي يكون فيها الطفل الأكبر أكثر وعياً بالتغيير. وبما أن القطع المخصصة تُصنع حسب الطلب، خططوا للطلب قبل شهر تقريباً نظراً لأن مواعيد الولادة تقريبية.',
		},
		{
			question: 'هل يجب تقديم هدية الأخ الكبير على أنها من المولود الجديد؟',
			answer:
				'عادة لا. يكتشف الأطفال الأكبر سناً هذه الحيلة غالباً، وقد تُفهم على أنها استخفاف بهم. تنجح الهدية بشكل أفضل حين تُقدَّم بصراحة من الشخص البالغ الذي يمنحها.',
		},
	],
	lang: 'ar',
	dateLocale: 'ar-EG',
	ui: {
		homeLabel: 'الرئيسية',
		blogLabel: 'المدونة',
		faqHeading: 'الأسئلة الشائعة',
		moreReadingHeading: 'مقالات أخرى',
		ctaLabel: 'تصفح الهدايا المخصصة',
	},
};

export default post;
